import { FeatureFlag } from '@/constants/featureFlags';
import type { PromoCodeData } from '@/services/iframeMessenger';
import type { ProviderEnum } from '@/types/common';
import type { AddressDetails } from '@/types/context';
import type { BasePaymentResponse } from '@/types/payment';
import { isIOSUserAgent, isLandscape, isMobileDevice } from './device';
import { isApplePayTabMode, APPLE_PAY_TAB_ROUTE } from './location';
import { logError, logInfo } from './logger';

type FeatureFlagMap = Partial<Record<FeatureFlag, boolean>> | Record<string, boolean>;

/**
 * Apple Pay in a cross-origin iframe is only supported from Safari 17 /
 * iOS 17 (`allow="payment"`). On iOS <= 16 `ApplePaySession` is not
 * exposed to the iframe at all, so the real button can never render.
 * See https://developer.apple.com/documentation/safari-release-notes/safari-17-release-notes
 */
export const MAX_IFRAME_BLOCKED_IOS_MAJOR_VERSION = 16;

/** postMessage handshake between the fallback tab and its opener (same origin). */
export const APPLE_PAY_TAB_READY_MESSAGE = 'appcharge:apple-pay-tab:ready';
export const APPLE_PAY_TAB_DATA_MESSAGE = 'appcharge:apple-pay-tab:data';
/**
 * Tab -> opener, the moment the payment made in the tab is authorized.
 *
 * This is the handover point of the whole flow: the tab exists only to host
 * the Apple Pay sheet, so as soon as the sheet authorizes it hands the result
 * back and closes. Everything after authorization — order polling, the
 * success screen, and the SDK lifecycle events the publisher listens for —
 * belongs to the framed checkout, which is the window the publisher's SDK is
 * attached to and the one the shopper returns to.
 */
export const APPLE_PAY_TAB_PAYMENT_AUTHORIZED_MESSAGE = 'appcharge:apple-pay-tab:payment-authorized';

/**
 * How long the fallback tab waits for the opener's checkout-data reply
 * before falling back to a regular `/checkout/v3` fetch (opener closed,
 * tab reloaded, etc.).
 */
export const APPLE_PAY_TAB_HANDOFF_TIMEOUT_MS = 4000;

export interface ApplePayTabDataMessage {
  type: typeof APPLE_PAY_TAB_DATA_MESSAGE;
  /**
   * `/checkout/v3` payload with `pricing` overlaid from live checkout
   * context — promo / ZIP-tax adjustments only ever land in context via
   * `updatePriceBreakdown`, never back in the query cache.
   */
  checkoutData: unknown;
  /** Promo applied in the opener, if any — for the tab's analytics events. */
  promoCode: PromoCodeData | null;
  /**
   * ZIP / address as edited in the opener. Travels with `pricing` for the
   * same reason: an edit lands in context only, so the cached customer
   * payload would show the tab a stale ZIP next to the updated tax total.
   */
  addressDetails: AddressDetails | null;
}

export interface ApplePayTabPaymentAuthorizedMessage {
  type: typeof APPLE_PAY_TAB_PAYMENT_AUTHORIZED_MESSAGE;
  /** Payment response the tab got back — drives the opener's success screen. */
  result: BasePaymentResponse;
  provider: ProviderEnum;
}

/**
 * Hands the authorized payment back to the opener and closes this tab — the
 * tab's whole job was the Apple Pay sheet. Self-guarding, so callers on the
 * shared checkout path don't need the branch: outside the fallback tab it is
 * a no-op.
 *
 * Returns whether the opener took ownership. `false` means this window is on
 * its own (opener closed, tab reloaded, payload not cloneable) and must run
 * the normal post-payment flow itself rather than close on a live order.
 */
export const handOffApplePayTabPaymentToOpener = (result: BasePaymentResponse, provider: ProviderEnum): boolean => {
  if (!isApplePayTabMode()) return false;
  const opener = window.opener as Window | null;
  if (!opener || opener.closed) return false;
  try {
    const message: ApplePayTabPaymentAuthorizedMessage = {
      type: APPLE_PAY_TAB_PAYMENT_AUTHORIZED_MESSAGE,
      result,
      provider,
    };
    // The message is queued on the opener before this window goes away, so
    // closing right after is safe — and it is what returns the shopper to the
    // checkout they started from.
    opener.postMessage(message, window.location.origin);
  } catch (error) {
    logError('Apple Pay tab fallback: failed to hand the authorized payment to the opener', error);
    return false;
  }
  logInfo('Apple Pay tab fallback: authorized payment handed back to the opener, closing tab');
  window.close();
  return true;
};

/**
 * Real iOS major version from the UA (`iPhone OS 16_6` / iPad `CPU OS 16_6`).
 * Returns `null` when no version is present — notably iPadOS 13+ Safari,
 * which masquerades as macOS and carries no iOS version. Callers must treat
 * `null` as "not known to be broken".
 */
export const getIOSMajorVersion = (): number | null => {
  const match = navigator.userAgent.match(/(?:iPhone|iPad|iPod).*? OS (\d+)_/i);
  return match ? Number(match[1]) : null;
};

const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws — definitely framed.
    return true;
  }
};

/**
 * Runtime latch: Apple rejected the merchant session for the page hosting
 * this frame (see `isApplePayDomainFailure`). Unlike the iOS <= 16 rule
 * this can only be learned by trying, so the flag flips mid-session and
 * every Apple Pay slot must re-render — hence the subscription below,
 * consumed through `useApplePayTabFallbackGate`.
 *
 * Deliberately in-memory: a reload replays the one failing attempt before
 * latching again, which beats persisting a verdict in storage that Safari
 * partitions per top-level site anyway.
 */
let domainFailureDetected = false;
const domainFailureListeners = new Set<() => void>();

export const hasApplePayDomainFailure = (): boolean => domainFailureDetected;

export const markApplePayDomainFailure = (): void => {
  if (domainFailureDetected) return;
  domainFailureDetected = true;
  domainFailureListeners.forEach((notify) => notify());
};

export const subscribeToApplePayDomainFailure = (notify: () => void): (() => void) => {
  domainFailureListeners.add(notify);
  return () => {
    domainFailureListeners.delete(notify);
  };
};

/** Test-only: clears the latch (listeners survive, they unsubscribe on unmount). */
export const resetApplePayDomainFailure = (): void => {
  domainFailureDetected = false;
};

/**
 * Runtime latch: inside the fallback tab, Apple Pay reported that it cannot
 * run HERE either — the PSP answered `unavailable` (an unregistered payment
 * method domain, a device with no Wallet, a browser without the Apple Pay JS
 * API) or the support probe stripped the method.
 *
 * This is the end of the road, which is why it gets its own flag rather than
 * reusing the domain-failure latch: that one opens the gate to the fake
 * button, and here there is nothing left to open. The tab is already
 * top-level, so a second tab would land on this same domain. All the screen
 * can do is say so, instead of collapsing the button into blank space.
 *
 * Self-guarding on tab mode, so the shared unavailable paths can call it
 * without branching.
 */
let tabWalletUnusable = false;
const tabWalletUnusableListeners = new Set<() => void>();

export const isApplePayTabUnusable = (): boolean => tabWalletUnusable;

export const markApplePayTabUnusable = (): void => {
  if (!isApplePayTabMode()) return;
  if (tabWalletUnusable) return;
  tabWalletUnusable = true;
  logInfo('Apple Pay tab fallback: Apple Pay is unavailable inside the tab as well — nothing left to fall back to');
  tabWalletUnusableListeners.forEach((notify) => notify());
};

export const subscribeToApplePayTabUnusable = (notify: () => void): (() => void) => {
  tabWalletUnusableListeners.add(notify);
  return () => {
    tabWalletUnusableListeners.delete(notify);
  };
};

/** Test-only: clears the latch (listeners survive, they unsubscribe on unmount). */
export const resetApplePayTabUnusable = (): void => {
  tabWalletUnusable = false;
};

/**
 * iOS <= 16 never exposes `ApplePaySession` to a cross-origin frame, so
 * the real button is known-dead before it is ever rendered.
 */
const isIOSVersionIframeBlocked = (): boolean => {
  if (!isIOSUserAgent()) return false;
  const majorVersion = getIOSMajorVersion();
  return majorVersion !== null && majorVersion <= MAX_IFRAME_BLOCKED_IOS_MAJOR_VERSION;
};

export type ApplePayTabFallbackReason = 'ios_iframe_blocked' | 'domain_failure';

const isTabFallbackKillSwitchedOff = (featureFlags?: FeatureFlagMap): boolean =>
  (featureFlags as Partial<Record<FeatureFlag, boolean>>)?.[FeatureFlag.APPLE_PAY_TAB_FALLBACK] === false;

/**
 * Shared preconditions for either trigger: framed phone (or landscape band),
 * not inside the fallback tab itself, kill switch not off.
 */
const isTabFallbackEnvironment = (featureFlags?: FeatureFlagMap): boolean => {
  if (isTabFallbackKillSwitchedOff(featureFlags)) return false;
  if (isApplePayTabMode()) return false;
  if (!isInIframe()) return false;
  if (!isMobileDevice() && !isLandscape()) return false;
  return true;
};

/**
 * Why the fallback is on, or `null` when it is off. Requires a phone
 * (portrait or the landscape band) inside a frame on iOS, and one of two
 * triggers — both of which mean the REAL button is known-dead:
 *   - `ios_iframe_blocked`: iOS <= 16 — `ApplePaySession` is absent in the
 *     frame, so this is known before first paint.
 *   - `domain_failure`: latched after the PSP told us Apple Pay cannot render
 *     or Apple rejected the merchant session — typically an embedding domain
 *     that is not Apple-Pay registered. Every provider path arms this on its
 *     unavailable branch (`armApplePayTabFallbackIfEligible`).
 *
 * Framed iOS 17+ on its own is NOT a trigger: `allow="payment"` makes
 * in-iframe Apple Pay work whenever the parent domain IS registered, and
 * there the real sheet is one tap instead of a whole new tab. Treating every
 * framed iOS phone as broken swapped in the fake button for publishers whose
 * domain was fine — so the verdict is left to the provider probe, which is
 * the only thing that actually knows.
 *
 * Inside the fallback tab itself the checkout is top-level, so the gate is
 * off and the real provider button renders.
 *
 * `apple_pay_tab_fallback` is a soft kill switch (NUVEI_WALLETS convention):
 * missing/undefined keeps the fallback ON; explicit `false` disables it.
 */
export const getApplePayTabFallbackReason = (featureFlags?: FeatureFlagMap): ApplePayTabFallbackReason | null => {
  if (!isTabFallbackEnvironment(featureFlags)) return null;
  if (!isIOSUserAgent()) return null;
  if (isIOSVersionIframeBlocked()) return 'ios_iframe_blocked';
  if (hasApplePayDomainFailure()) return 'domain_failure';
  return null;
};

export const shouldUseApplePayTabFallback = (featureFlags?: FeatureFlagMap): boolean =>
  getApplePayTabFallbackReason(featureFlags) !== null;

/**
 * When the real Apple Pay button cannot appear (ECE / `isAvailable` /
 * `canMakePayments` said no — typically because the embedding page's
 * domain is not Apple-Pay verified), arm the domain-failure latch so the
 * fake new-tab button still renders. Returns whether the gate is now open.
 *
 * This is the ONLY way the gate opens on iOS 17+, so callers that would
 * otherwise report `unavailable` / strip Apple Pay must short-circuit on
 * `true`: the fallback reports `onReady(true)` itself and must not lose the
 * method from the picker.
 */
export const armApplePayTabFallbackIfEligible = (featureFlags?: FeatureFlagMap): boolean => {
  if (!isTabFallbackEnvironment(featureFlags)) return false;
  if (!isIOSUserAgent()) return false;
  markApplePayDomainFailure();
  return shouldUseApplePayTabFallback(featureFlags);
};

/**
 * URL for the fallback tab: same session token (path) + the original
 * query string and `#boot=` hash carried verbatim, so `getBoot()` and
 * `apiClient.initilize` work unchanged in the tab.
 */
export const buildApplePayTabUrl = (sessionToken: string): string =>
  `${window.location.origin}/${APPLE_PAY_TAB_ROUTE}/${sessionToken}${window.location.search}${window.location.hash}`;
